import React from 'react';
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router-dom';
import SignIn from '../pages/SignIn';
import HomePage from '../pages/HomePage';
import Logout from '../pages/Logout';
import IntegrationDetailPage from '../pages/IntegrationDetailPage';
import IntegrationsPage from '../pages/IntegrationsPage';
import IntegrationCreatePage from '../pages/IntegrationCreatePage';
import LocationSettingsPage from '../pages/LocationSettingsPage';
import CommandPage from '../pages/CommandsPage';
import LogInspectorPage from '../pages/LogInspectorPage';
import { encryptStorage } from '../utils/storage';
import ArchivePage from '../pages/ArchivePage';
import IntegrationEditPage from '../pages/IntegrationEditPage';
import ActionsPage from '../pages/ActionsPage';
import CommandErrorsPage from '../pages/CommandErrorsPage';
import WmiCodesPage from '../pages/WmiCodesPage';
import IntegrationLambdasPage from '../pages/IntegrationLambdasPage';
import IntegrationHealthPage from '../pages/IntegrationHealthPage';
import ProfilePage from '../pages/ProfilePage';
import SharedSettingsPage from '../pages/SharedSettingsPage';
import SharedSettingNewPage from '../pages/SharedSettingsPage/SharedSettingNewPage';
import SharedSettingEditPage from '../pages/SharedSettingsPage/SharedSettingEditPage';
import NeedsAttentionPage from '../pages/SharedSettingsPage/NeedsAttentionPage';
import WhatsNewPage from '../pages/WhatsNewPage';

function Routes() {
  // The guard below reads encrypted storage, which is not reactive. Without
  // subscribing to the location, this component never re-runs on navigation —
  // React bails out of re-rendering `children` when only context changed — so
  // the route elements stay frozen at the signed-out state and a fresh login
  // bounces straight back to /sign_in. @reach/router re-rendered its children.
  const { search } = useLocation();

  // @reach/router appended the current query to every redirect target
  // (insertParams reads location.search). v6's Navigate takes the string as
  // given, which silently dropped the `?sso_token=` Okta returns with — the SSO
  // round trip landed on a bare /sign_in and showed the form again.
  const withQuery = (path) => `${path}${search}`;

  const userSession = encryptStorage.getItem('userSession');
  const authToken = encryptStorage.getItem('authToken');

  const routes = [
    { component: IntegrationsPage, path: '/integrations' },
    { component: IntegrationDetailPage, path: '/integrations/:identifier' },
    { component: IntegrationCreatePage, path: '/integrations/create' },
    { component: LocationSettingsPage, path: '/location_settings/:identifier' },
    { component: CommandPage, path: '/toolkit/commands' },
    { component: LogInspectorPage, path: '/toolkit/log_inspector/*' },
    { component: ArchivePage, path: '/integrations/archive' },
    { component: IntegrationEditPage, path: '/integrations/:id/edit' },
    { component: ActionsPage, path: '/toolkit/actions' },
    { component: CommandErrorsPage, path: '/toolkit/errors' },
    { component: WmiCodesPage, path: '/toolkit/wmi_codes' },
    {
      component: IntegrationLambdasPage,
      path: '/toolkit/integration_lambdas',
    },
    {
      component: IntegrationHealthPage,
      path: '/toolkit/integration_health',
    },
    { component: ProfilePage, path: '/user' },
    { component: SharedSettingsPage, path: '/toolkit/shared_settings' },
    { component: SharedSettingNewPage, path: '/toolkit/shared_settings/new' },
    // Declared before the :uuid route so the literal segment is not swallowed by it.
    {
      component: NeedsAttentionPage,
      path: '/toolkit/shared_settings/:uuid/needs_attention',
    },
    {
      component: SharedSettingEditPage,
      path: '/toolkit/shared_settings/:uuid',
    },
    { component: WhatsNewPage, path: '/whats-new' },
  ];

  const signedIn = Boolean(userSession && authToken);

  return (
    <div>
      <RouterRoutes>
        <Route
          path="/sign_in"
          element={
            // Must match the guard the protected routes use below: redirecting
            // on userSession alone bounces a session that kept the user but lost
            // the token straight back here, in a loop.
            signedIn ? (
              <Navigate replace to={withQuery('/integrations')} />
            ) : (
              <SignIn />
            )
          }
        />
        <Route path="/logout" element={<Logout />} />

        {/* HomePage is the shell; its children render through an <Outlet />. */}
        <Route path="/" element={<HomePage />}>
          <Route
            index
            element={<Navigate replace to={withQuery('/integrations')} />}
          />
          {routes.map(({ component: Component, path }) => (
            <Route
              key={path}
              path={path}
              element={
                signedIn ? (
                  <Component />
                ) : (
                  <Navigate replace to={withQuery('/sign_in')} />
                )
              }
            />
          ))}
        </Route>
      </RouterRoutes>
    </div>
  );
}

export default Routes;
